/**
 * Central API client.
 *
 * When VITE_DISABLE_AUTH=true (local dev), requests are sent without an
 * Authorization header — pair this with REQUIRE_AUTH=false on the backend
 * services.
 *
 * When auth is enabled, wire in Firebase getIdToken() here.
 */

function viteApiUrl(envKey: string, fallback: string): string {
  const raw = import.meta.env[envKey] as string | undefined;
  return raw?.trim() ? raw : fallback;
}

const FACT_CHECKER_URL = viteApiUrl(
  'VITE_FACT_CHECKER_API_URL',
  '/api/fact-checker',
);

const CHECKLIST_URL = viteApiUrl('VITE_CHECKLIST_API_URL', '/api/checklist');

const CONTENT_GEN_URL = viteApiUrl(
  'VITE_CONTENT_GEN_API_URL',
  '/api/content-gen',
);

const CRITIC_URL = viteApiUrl('VITE_CRITIC_API_URL', '/api/critic');

const DISABLE_AUTH =
  (import.meta.env.VITE_DISABLE_AUTH as string | undefined) === 'true';

async function getAuthHeaders(): Promise<Record<string, string>> {
  if (DISABLE_AUTH) return {};
  // TODO: replace with Firebase getIdToken() when auth is enabled
  // const token = await firebase.auth().currentUser?.getIdToken();
  // return token ? { Authorization: `Bearer ${token}` } : {};
  return {};
}

async function request<T>(
  baseUrl: string,
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const authHeaders = await getAuthHeaders();
  const url = `${baseUrl}${path}`;

  let res: Response;
  try {
    res = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...(options.headers ?? {}),
      },
    });
  } catch (err: unknown) {
    const detail = err instanceof Error ? err.message : String(err);
    throw new Error(
      `Could not reach service at ${url}: ${detail}. ` +
        'Is the service running and the Vite dev server restarted?',
    );
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status} ${res.statusText}: ${text}`);
  }

  // 204 No Content — nothing to parse
  if (res.status === 204) return undefined as T;

  return (await res.json()) as T;
}

export const factCheckerApi = {
  post: <T>(path: string, body: unknown) =>
    request<T>(FACT_CHECKER_URL, path, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  get: <T>(path: string) =>
    request<T>(FACT_CHECKER_URL, path, { method: 'GET' }),
};

export const checklistApi = {
  post: <T>(path: string, body: unknown) =>
    request<T>(CHECKLIST_URL, path, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  get: <T>(path: string) =>
    request<T>(CHECKLIST_URL, path, { method: 'GET' }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(CHECKLIST_URL, path, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  delete: (path: string) =>
    request<void>(CHECKLIST_URL, path, { method: 'DELETE' }),
};

export const contentGenApi = {
  post: <T>(path: string, body: unknown) =>
    request<T>(CONTENT_GEN_URL, path, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  put: <T>(path: string, body: unknown) =>
    request<T>(CONTENT_GEN_URL, path, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  get: <T>(path: string) =>
    request<T>(CONTENT_GEN_URL, path, { method: 'GET' }),
  delete: (path: string) =>
    request<void>(CONTENT_GEN_URL, path, { method: 'DELETE' }),
};

export const criticApi = {
  post: <T>(path: string, body: unknown) =>
    request<T>(CRITIC_URL, path, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  get: <T>(path: string) =>
    request<T>(CRITIC_URL, path, { method: 'GET' }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(CRITIC_URL, path, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  delete: (path: string) =>
    request<void>(CRITIC_URL, path, { method: 'DELETE' }),
};
